// src/pages/Progress.jsx
import React, { useState, useEffect, useMemo } from "react";
import { DailyCheckIn, User, UserProgress } from "@/api/entities";
import { Award, Calendar, ChevronLeft, ChevronRight, Cigarette, Euro, Target, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import ProgressCalendar from "@/components/progress/ProgressCalendar";
import DayDetailModal from "@/components/progress/DayDetailModal";
import { format, addMonths, subMonths, add } from "date-fns";
import { it } from "date-fns/locale";
import { saveBackdatedCheckinAndRefresh } from "@/lib/progress-snapshot"; // <-- AGGIUNTO

// ----- utils & debug -----
const dbg = (...a) => { if (localStorage.getItem("rb_debug") === "1") console.log("[progress]", ...a); };

// normalizza a mezzogiorno per evitare offset / off-by-one
const toNoon = (d) => {
  const x = new Date(typeof d === "string" ? (d.includes("T") ? d : `${d}T12:00:00`) : d);
  x.setHours(12, 0, 0, 0);
  return x;
};

// normalizzazione tollerante a formati diversi
function normCheckIn(ci) {
  if (!ci) return null;
  const dateSrc = ci.date || ci.day || ci.created_at || ci.now;
  const date = String(dateSrc || "").slice(0, 10);

  const raw = ci.status || ci.q3_breathing || (ci.relapse ? "relapse" : "");
  const status = /relapse|fail|ricad/i.test(raw) ? "relapse" : /skip|none/i.test(raw) ? "skip" : "success";

  const cigarettes_avoided = Number(ci.cigarettes_avoided ?? ci.cigs_avoided ?? ci.cigs ?? 0) || 0;
  const savings_eur        = Number(ci.savings_eur ?? ci.eur ?? ci.savings ?? 0) || 0;

  return {
    ...ci,
    date,
    status,
    q3_breathing: ci.q3_breathing ?? status,
    relapse_cigarettes_smoked: Number(ci.relapse_cigarettes_smoked ?? ci.relapse_cigs ?? 0) || 0,
    cigarettes_avoided,
    savings_eur,
    note: ci.note || ci.notes || "",
  };
}

// identità robusta per le query (email/id da User.me o da LS)
async function getUserSafe() {
  try {
    const u = await User.me();
    if (u?.email) localStorage.setItem("rb_user_email", u.email);
    if (u?.id)    localStorage.setItem("rb_user_id", u.id);
    return u || null;
  } catch { return null; }
}

// baseline letti in modo coerente col Check-in (più fallback)
function getBaselines(user) {
  const b =
    Number(user?.baseline_cigs_per_day) ||
    Number(user?.cigarettes_per_day) ||
    Number(localStorage.getItem("rb_baseline_cpd")) || 0;

  const p =
    Number(user?.pack_price_eur) ||
    Number(user?.pack_price) ||
    Number(localStorage.getItem("rb_pack_price_eur")) || 0;

  const cpp =
    Number(user?.cigs_per_pack) ||
    Number(user?.cigarettes_per_pack) ||
    Number(localStorage.getItem("rb_cigs_per_pack")) || 0;

  return {
    baselineCpd: b,
    packPrice: p,
    cigsPerPack: cpp,
    pricePerCig: (p > 0 && cpp > 0) ? p / cpp : 0,
  };
}

// calcola le metriche per un singolo check-in (usa fallback se mancano i campi espliciti)
function metricsFor(ci, baselineCpd, pricePerCig) {
  let avoided = Number(ci.cigarettes_avoided || ci.cigs_avoided || 0);
  if (!avoided && baselineCpd) {
    avoided = ci.q3_breathing === "relapse"
      ? Math.max(0, baselineCpd - Number(ci.relapse_cigarettes_smoked || 0))
      : baselineCpd;
  }
  let saved = Number(ci.savings_eur || ci.eur || 0);
  if (!saved && pricePerCig) saved = avoided * pricePerCig;

  return {
    avoided: Math.round(avoided || 0),
    saved: Number(Number(saved || 0).toFixed(2)),
  };
}

// ⬇️ canonizza le etichette obiettivi per l’esercizio 6 (supporta anche short keys tipo "1d", "3w", ecc.)
const canonGoalLabel = (k) => {
  const s = String(k || "").toLowerCase().trim();
  const code = s.replace(/\s+/g, "");
  // short codes
  const short = {
    "1d": "1 Giorno", "3d": "3 Giorni",
    "1w": "1 Settimana", "2w": "2 Settimane", "3w": "3 Settimane",
    "1m": "1 Mese", "2m": "2 Mesi", "6m": "6 Mesi",
    "1y": "1 Anno", "2y": "2 Anni", "5y": "5 Anni", "10y": "10 Anni",
  };
  if (short[code]) return short[code];

  // textual labels
  if (/^1\b.*giorn/.test(s) || (/giorn/.test(s) && /(^|[^0-9])1([^0-9]|$)/.test(s))) return "1 Giorno";
  if (/^3\b.*giorn/.test(s) || (/giorn/.test(s) && /3/.test(s))) return "3 Giorni";
  if (/^1\b.*settim/.test(s) || (/settim/.test(s) && /1/.test(s))) return "1 Settimana";
  if (/^2\b.*settim/.test(s) || (/settim/.test(s) && /2/.test(s))) return "2 Settimane";
  if (/^3\b.*settim/.test(s) || (/settim/.test(s) && /3/.test(s))) return "3 Settimane";
  if (/^1\b.*mes/.test(s)   || (/mes/.test(s)   && /(^|[^0-9])1([^0-9]|$)/.test(s))) return "1 Mese";
  if (/^2\b.*mes/.test(s)   || (/mes/.test(s)   && /2/.test(s))) return "2 Mesi";
  if (/^6\b.*mes/.test(s)   || (/mes/.test(s)   && /6/.test(s))) return "6 Mesi";
  if (/^1\b.*ann/.test(s)   || (/ann/.test(s)   && /(^|[^0-9])1([^0-9]|$)/.test(s))) return "1 Anno";
  if (/^2\b.*ann/.test(s)   || (/ann/.test(s)   && /2/.test(s))) return "2 Anni";
  if (/^5\b.*ann/.test(s)   || (/ann/.test(s)   && /5/.test(s))) return "5 Anni";
  if (/^10\b.*ann/.test(s)  || (/ann/.test(s)  && /10/.test(s))) return "10 Anni";
  return null;
};

export default function ProgressPage() {
  const [user, setUser] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // retro-checkin modal
  const [showBackfill, setShowBackfill] = useState(false);
  const [bf, setBf] = useState({ date: format(new Date(), "yyyy-MM-dd"), status: "success", smoked: 0 });
  const [savingBf, setSavingBf] = useState(false);

  useEffect(() => {
    loadProgressData();

    const onSaved = () => loadProgressData();
    window.addEventListener("rb:checkin-saved", onSaved);
    window.addEventListener("focus", onSaved);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onSaved();
    });
    return () => {
      window.removeEventListener("rb:checkin-saved", onSaved);
      window.removeEventListener("focus", onSaved);
    };
  }, []);

  async function loadProgressData() {
    setIsLoading(true);
    try {
      const u = await getUserSafe();
      setUser(u);

      const lsEmail = localStorage.getItem("rb_user_email") || null;
      const lsId    = localStorage.getItem("rb_user_id") || null;

      const keysToTry = [
        u?.email && { created_by: u.email },
        u?.id    && { created_by: u.id },
        lsEmail  && { created_by: lsEmail },
        lsId     && { created_by: lsId },
      ].filter(Boolean);

      let allCheckIns = [];
      for (const query of keysToTry) {
        try {
          const res = await DailyCheckIn.filter(query, "-date");
          if (Array.isArray(res) && res.length) { allCheckIns = res; break; }
        } catch (e) { dbg("filter fail for", query, e); }
      }

      if (!allCheckIns.length) {
        try { allCheckIns = await DailyCheckIn.filter({}, "-date"); } catch {}
      }

      let progress = [];
      for (const query of keysToTry) {
        try {
          const res = await UserProgress.filter(query);
          if (Array.isArray(res) && res.length) { progress = res; break; }
        } catch {}
      }

      const normalized = (Array.isArray(allCheckIns) ? allCheckIns : [])
        .map(normCheckIn)
        .filter(Boolean);

      const dedup = Object.values(
        normalized.reduce((acc, ci) => {
          const k = ci.id || ci.date;
          if (!acc[k]) acc[k] = ci;
          return acc;
        }, {})
      );

      setCheckIns(dedup);
      setUserProgress(Array.isArray(progress) ? progress : []);

      if (typeof window !== "undefined") {
        window.__rb_dumpProgress = () => ({ user: u, checkIns: dedup, userProgress: progress });
      }

      dbg("loaded", { count: dedup.length });
    } catch (error) {
      console.error("Errore caricamento dati progressi:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // --- DEV HOOK: crea check-in via console anche in /progress ---
  useEffect(() => {
    if (typeof window === "undefined") return;

    window.__rb_addCheckins = async (items = []) => {
      const u = await getUserSafe();
      const lsEmail = localStorage.getItem("rb_user_email") || null;
      const lsId    = localStorage.getItem("rb_user_id") || null;
      const created_by = (u?.email || lsEmail) || (u?.id || lsId) || "anon";

      const { baselineCpd, pricePerCig } = getBaselines(u);
      const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
      const round2 = (x) => Math.round((num(x, 0) + Number.EPSILON) * 100) / 100;

      async function delOne(id) {
        const E = DailyCheckIn;
        const fns = ["delete", "remove", "destroy", "del"].filter(fn => typeof E?.[fn] === "function");
        for (const fn of fns) { try { await E[fn](id); return true; } catch {} }
        return false;
      }

      for (const it of items) {
        const dateStr = String(it.date).slice(0, 10);
        const smoked  = Math.max(0, num(it.smoked, 0));
        const relapse = smoked > 0;

        const avoided = Math.max(0, baselineCpd ? (relapse ? (baselineCpd - smoked) : baselineCpd) : 0);
        const saved   = pricePerCig ? round2(avoided * pricePerCig) : 0;

        try {
          const exist = await DailyCheckIn.filter({ created_by, date: dateStr });
          if (Array.isArray(exist) && exist.length) {
            for (const r of exist) { try { await delOne(r.id); } catch {} }
          }
        } catch {}

        const record = {
          date: dateStr,
          created_at: new Date().toISOString(),
          created_by,
          q1_feeling: 7,
          q2_motivation: 7,
          q3_breathing: relapse ? "relapse" : "yoga_relax",
          relapse_cigarettes_smoked: relapse ? smoked : 0,
          status: relapse ? "relapse" : "success",
          cigarettes_avoided: avoided,
          savings_eur: saved,
          cigs_avoided: avoided,
          eur: saved,
        };

        await DailyCheckIn.create(record);
        console.log("✅ creato", dateStr, record);
      }

      window.dispatchEvent(new Event("rb:checkin-saved"));
      return true;
    };

    return () => { try { delete window.__rb_addCheckins; } catch {} };
  }, []);

  // ---- calcolo carte totali (somma SOLO sui giorni con check-in) ----
  const savingsData = useMemo(() => {
    if (checkIns.length === 0) return { cigarettesAvoided: 0, totalSavings: 0 };

    const { baselineCpd, pricePerCig } = getBaselines(user);

    // se nessuna baseline, sommo solo i valori espliciti
    if (!baselineCpd || !pricePerCig) {
      const cigs = checkIns.reduce((s, ci) => s + Number(ci.cigarettes_avoided || ci.cigs_avoided || 0), 0);
      const eur  = checkIns.reduce((s, ci) => s + Number(ci.savings_eur || ci.eur || 0), 0);
      return { cigarettesAvoided: Math.round(cigs), totalSavings: Number(eur.toFixed(2)) };
    }

    // baseline presente → calcolo robusto per ciascun check-in
    const totals = checkIns.reduce(
      (acc, ci) => {
        const { avoided, saved } = metricsFor(ci, baselineCpd, pricePerCig);
        acc.cigs += avoided;
        acc.eur  += saved;
        return acc;
      },
      { cigs: 0, eur: 0 }
    );

    return { cigarettesAvoided: totals.cigs, totalSavings: Number(totals.eur.toFixed(2)) };
  }, [user, checkIns]);

  // ⬇️ REWARDS: leggi l’ultima configurazione dell’esercizio 6 (check-in o UserProgress)
  const rewardsMap = useMemo(() => {
    // helper parse
    const parse = (v) => {
      if (!v) return null;
      if (typeof v === "string") { try { return JSON.parse(v); } catch { return null; } }
      return (typeof v === "object") ? v : null;
    };

    // 1) dal check-in più recente che contiene ricompense
    const fromCi = [...checkIns]
      .reverse()
      .map(ci => ci.ricompense_obiettivi || ci.rewards || null)
      .find(o => o && typeof o === "object");

    let raw = fromCi;

    // 2) altrimenti da UserProgress lezione 6 (parsa sia oggetti che stringhe JSON)
    if (!raw) {
      const up = (Array.isArray(userProgress) ? userProgress : []).find(p => {
        const lid = String(p?.lesson_id ?? p?.lessonId ?? "");
        return lid === "6";
      });
      raw =
        parse(up?.rewards_map || up?.rewardsMap) ||
        parse(up?.exercise_rewards) ||
        parse(up?.exercise_answer || up?.exerciseAnswer) ||
        null;
    }

    const out = {};
    if (raw && typeof raw === "object") {
      for (const [k, v] of Object.entries(raw)) {
        const label = canonGoalLabel(k) || canonGoalLabel(String(v?.label || ""));
        if (!label) continue;
        const text = typeof v === "object" ? (v.text ?? v.value ?? v.label ?? "") : String(v ?? "");
        if (text) out[label] = text.trim();
      }
    }
    return out; // { "1 Giorno": "pizza", "1 Settimana": "wagyu", ... }
  }, [checkIns, userProgress]);

  // ---- calendar + obiettivi ----
  const periodiObiettivi = useMemo(
    () => [
      { duration: { days: 1 }, label: "1 Giorno" },
      { duration: { days: 3 }, label: "3 Giorni" },
      { duration: { days: 7 }, label: "1 Settimana" },
      { duration: { days: 14 }, label: "2 Settimane" },
      { duration: { months: 1 }, label: "1 Mese" },
      { duration: { months: 2 }, label: "2 Mesi" },
      { duration: { months: 6 }, label: "6 Mesi" },
      { duration: { years: 1 }, label: "1 Anno" },
      { duration: { years: 2 }, label: "2 Anni" },
      { duration: { years: 5 }, label: "5 Anni" },
      { duration: { years: 10 }, label: "10 Anni" },
    ],
    []
  );

  const processedData = useMemo(() => {
    const data = {};
    let streak = 0;
    const achievements = new Set();
    let upcomingGoals = [];

    const today = new Date();
    today.setHours(12, 0, 0, 0);

    const { baselineCpd, pricePerCig } = getBaselines(user);

    if (checkIns.length === 0) {
      periodiObiettivi.forEach(period => {
        const goalDate = add(today, period.duration);
        upcomingGoals.push({ date: goalDate, label: period.label, reward: rewardsMap[period.label] });
      });
      upcomingGoals = upcomingGoals
        .filter(goal => goal.date > today)
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      return { calendarData: {}, achievements: [], upcomingGoals };
    }

    const sorted = [...checkIns].sort((a, b) => new Date(a.date) - new Date(b.date));
    const lastRelapseIndex = sorted.map(ci => ci.q3_breathing === "relapse").lastIndexOf(true);

    let startDateForGoals;
    if (lastRelapseIndex === -1) {
      startDateForGoals = toNoon(sorted[0].date);
    } else {
      const firstPositiveAfterRelapse = sorted.find((ci, index) => index > lastRelapseIndex && ci.q3_breathing !== "relapse");
      startDateForGoals = firstPositiveAfterRelapse ? toNoon(firstPositiveAfterRelapse.date) : toNoon(today);
    }

    periodiObiettivi.forEach(period => {
      const goalDate = add(startDateForGoals, period.duration);
      const goalDateStr = format(goalDate, "yyyy-MM-dd");
      if (!data[goalDateStr]) data[goalDateStr] = { activities: {} };
      // ⬇️ aggiungo la ricompensa (se configurata)
      data[goalDateStr].goal = { label: period.label, reward: rewardsMap[period.label] };
      upcomingGoals.push({ date: goalDate, label: period.label, reward: rewardsMap[period.label] });
    });

    for (const checkIn of sorted) {
      const dateStr = checkIn.date;
      const idx = sorted.findIndex(c => c.id === checkIn.id);
      const isAfterLastRelapse = idx > lastRelapseIndex;

      if (checkIn.q3_breathing === "relapse")      streak = 0;
      else if (isAfterLastRelapse)                 streak++;
      else                                         streak = 0;

      if (!data[dateStr]) data[dateStr] = { activities: {} };
      const base = data[dateStr];

      const { avoided, saved } = metricsFor(checkIn, baselineCpd, pricePerCig);
      const enrichedCheckIn = {
        ...checkIn,
        cigarettes_avoided: avoided,
        savings_eur: saved,
        cigs_avoided: avoided,
        eur: saved,
      };

      data[dateStr] = {
        ...base,
        checkIn: enrichedCheckIn,
        dailyCheckIn: enrichedCheckIn,
        has_checkin: true,
        status: enrichedCheckIn.status || enrichedCheckIn.q3_breathing,
        metrics: { cigarettes_avoided: avoided, savings_eur: saved },
        note: enrichedCheckIn.note || base.note,
        activities: {
          ...base.activities,
          check_up: true,
          esercizio_5_completato: enrichedCheckIn.esercizio_5_completato,
          esercizio_7_completato: enrichedCheckIn.esercizio_7_completato,
          esercizio_9_completato: enrichedCheckIn.esercizio_9_completato,
          abitudini_potenzianti_completate: enrichedCheckIn.abitudini_potenzianti_completate
        },
        streak
      };

      if (streak >= 1) achievements.add("1 Giorno");
      if (streak >= 3) achievements.add("3 Giorni");
      if (streak >= 7) achievements.add("1 Settimana");
      if (streak >= 14) achievements.add("2 Settimane");
      if (streak >= 30) achievements.add("1 Mese");
      if (streak >= 60) achievements.add("2 Mesi");
      if (streak >= 180) achievements.add("6 Mesi");
      if (streak >= 365) achievements.add("1 Anno");
      if (streak >= 730) achievements.add("2 Anni");
      if (streak >= 1825) achievements.add("5 Anni");
      if (streak >= 3650) achievements.add("10 Anni");
    }

    // ⬇️ mostra solo obiettivi futuri NON ancora raggiunti
    const now = new Date();
    now.setHours(12, 0, 0, 0);

    upcomingGoals = upcomingGoals
      .filter(goal => goal.date > now && !achievements.has(goal.label))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return { calendarData: data, achievements: Array.from(achievements), upcomingGoals };
  }, [checkIns, userProgress, periodiObiettivi, user, rewardsMap]);

  const { calendarData, achievements, upcomingGoals } = processedData;
  const longestStreak = Math.max(0, ...Object.values(calendarData).map(d => d.streak || 0));

  const handleDayClick = day => {
    const key = format(day, "yyyy-MM-dd");
    if (typeof window !== "undefined") {
      window.__lastDayKey = key;
      window.__lastDayData = calendarData?.[key] ?? null;
    }
    setSelectedDate(day);
  };

  const formatNumber = n => new Intl.NumberFormat("it-IT").format(n);
  const formatCurrency = n => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);

  // ---- salvataggio check-in retroattivo ----
  async function saveBackfill() {
    try {
      setSavingBf(true);
      const dateStr = String(bf.date).slice(0, 10);
      const relapse = bf.status === "relapse";
      const smoked  = relapse ? Math.max(0, Number(bf.smoked || 0)) : 0;

      await saveBackdatedCheckinAndRefresh({
        date: dateStr,
        status: relapse ? "relapse" : "success",
        relapse_cigarettes_smoked: smoked,
        note: "[retro dalla UI]",
      });

      setShowBackfill(false);
    } catch (e) {
      console.error("Errore backfill:", e);
    } finally {
      setSavingBf(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          I Tuoi <span className="bg-gradient-to-r from-green-500 to-blue-600 bg-clip-text text-transparent">Progressi</span>
        </h1>

        {/* Savings Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-green-400 to-blue-500 text-white shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro />
                Risparmi Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold">{formatCurrency(savingsData.totalSavings)}</p>
              <p className="text-blue-100">dal tuo primo giorno!</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-700 to-gray-900 text-white shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cigarette />
                Sigarette Evitate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold">{formatNumber(savingsData.cigarettesAvoided)}</p>
              <p className="text-gray-300">e continui a salire!</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content: Calendar */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Calendar className="w-6 h-6" />
                    Diario dei Progressi
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowBackfill(true)}>
                      <Plus className="w-4 h-4 mr-1" /> Aggiungi check-in (retro)
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="font-semibold w-32 text-center">{format(currentMonth, "MMMM yyyy", { locale: it })}</span>
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ProgressCalendar
                  data={calendarData}
                  onDayClick={handleDayClick}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                />
                <div className="flex flex-wrap justify-end gap-4 mt-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full" /> Giorno di successo
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full" /> Giorno di ricaduta
                  </div>
                  <div className="flex items-center gap-1">
                    <Award className="w-3 h-3 text-yellow-500" /> Traguardo
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side content: Stats */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-xl">
              <CardHeader>
                <CardTitle>Serie di Successi</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-5xl font-bold">{longestStreak}</p>
                <p className="text-blue-100">giorni di fila dall'ultima ricaduta</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target />
                  Prossimi Obiettivi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingGoals.length > 0 ? (
                  upcomingGoals.slice(0, 4).map((goal, index) => (
                    <div key={goal.label + index} className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-gray-700">
                        {goal.label}
                        {goal.reward ? <span className="ml-2 text-xs text-gray-500">— {goal.reward}</span> : null}
                      </span>
                      <Badge variant="outline">{format(goal.date, "d MMM", { locale: it })}</Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Nessun nuovo obiettivo in vista. Continua così!</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award />
                  Obiettivi Raggiunti
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {achievements.length > 0 ? (
                  achievements.map(ach => <Badge key={ach} className="bg-green-100 text-green-800">{ach}</Badge>)
                ) : (
                  <p className="text-sm text-gray-500">Continua così per sbloccare obiettivi!</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* MODALE: Aggiungi check-in retroattivo */}
      {showBackfill && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-lg font-semibold mb-4">Aggiungi check-in retroattivo</h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={bf.date} onChange={e => setBf(v => ({ ...v, date: e.target.value }))} />
              </div>

              <div className="space-y-2">
                <Label>Esito del giorno</Label>
                <RadioGroup
                  value={bf.status}
                  onValueChange={(v) => setBf(x => ({ ...x, status: v }))}
                  className="flex gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="success" id="bf-success" />
                    <Label htmlFor="bf-success">Successo</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="relapse" id="bf-relapse" />
                    <Label htmlFor="bf-relapse">Ricaduta</Label>
                  </div>
                </RadioGroup>
              </div>

              {bf.status === "relapse" && (
                <div className="space-y-2">
                  <Label>Sigarette fumate</Label>
                  <Input type="number" min="0" step="1"
                         value={bf.smoked}
                         onChange={e => setBf(v => ({ ...v, smoked: e.target.value }))} />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBackfill(false)}>Annulla</Button>
              <Button onClick={saveBackfill} disabled={savingBf}>{savingBf ? "Salvo..." : "Salva"}</Button>
            </div>
          </div>
        </div>
      )}

      <DayDetailModal
        date={selectedDate}
        dayData={selectedDate ? (calendarData[format(selectedDate, "yyyy-MM-dd")] || null) : null}
        onClose={() => setSelectedDate(null)}
      />
    </div>
  );
}
